import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import type { ClientSummary, InvoiceSummary, TimeEntrySummary } from '../types';

type EntriesResp = {
  items: Array<
    TimeEntrySummary & {
      case: { id: string; caseNumber: string; area: string | null; hourlyRateIls: number | null } | null;
    }
  >;
};

const VAT_RATE = 0.17;

export function CreateInvoiceModal({
  clients,
  onCreated,
  onClose,
}: {
  clients: ClientSummary[];
  onCreated: (invoice: InvoiceSummary) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation(['billing', 'common']);
  const [clientId, setClientId] = useState('');
  const [entries, setEntries] = useState<EntriesResp['items']>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lang, setLang] = useState<'he' | 'en'>(i18n.language.startsWith('he') ? 'he' : 'en');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pull unbilled, billable entries for the chosen client. We get them via
  // the case path: GET /api/clients/:id has cases; then /api/time-entries
  // filtered by case+unbilled. Simpler approach: GET /api/time-entries
  // unfiltered (server-side firm-scoped), then filter client-side by the
  // case's clientId.
  useEffect(() => {
    if (!clientId) {
      setEntries([]);
      setSelected(new Set());
      return;
    }
    setError(null);
    (async () => {
      try {
        // We don't have a clientId filter on /time-entries; fetch all
        // unbilled and filter by the cases the client owns.
        const [allEntries, clientDetail] = await Promise.all([
          apiFetch<EntriesResp>('/api/time-entries?unbilled=true&billable=true'),
          apiFetch<{ cases: Array<{ id: string }> }>(`/api/clients/${clientId}`),
        ]);
        const caseIds = new Set(clientDetail.cases.map((c) => c.id));
        const filtered = allEntries.items.filter(
          (e) => e.caseId && caseIds.has(e.caseId),
        );
        setEntries(filtered);
        setSelected(new Set(filtered.map((e) => e.id)));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [clientId]);

  const totals = useMemo(() => {
    const chosen = entries.filter((e) => selected.has(e.id));
    let totalShekels = 0;
    let totalMinutes = 0;
    for (const e of chosen) {
      const rate = e.hourlyRateIls ?? e.case?.hourlyRateIls ?? 0;
      totalShekels += (e.minutes / 60) * rate;
      totalMinutes += e.minutes;
    }
    const withVat = totalShekels * (1 + VAT_RATE);
    return {
      totalShekels: +withVat.toFixed(2),
      hours: +(totalMinutes / 60).toFixed(2),
      count: chosen.length,
    };
  }, [entries, selected]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (selected.size === 0) return;
    setBusy(true);
    setError(null);
    try {
      const inv = await apiFetch<InvoiceSummary>('/api/invoices/from-time-entries', {
        method: 'POST',
        body: JSON.stringify({
          clientId,
          timeEntryIds: Array.from(selected),
          lang,
          sendByEmail: true,
        }),
      });
      onCreated(inv);
    } catch (err) {
      const code =
        err instanceof ApiError ? err.code : err instanceof Error ? err.message : 'unknown';
      const errorKey = `errors.${code}`;
      const translated = t(errorKey);
      setError(translated === errorKey ? t('common:errors.with_message', { message: code }) : translated);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(ev) => ev.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          <div className="form-card-header">
            <div style={{ flex: 1 }}>
              <div className="form-preview-name">{t('create.title')}</div>
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label={t('common:actions.close')}
            >
              ×
            </button>
          </div>

          <div className="form-section">
            <div className="field-grid">
              <div className="field">
                <label>{t('create.client')} <span className="req">*</span></label>
                <select value={clientId} onChange={(e) => setClientId(e.target.value)} required>
                  <option value="">{t('create.select_client')}</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>{t('create.lang')}</label>
                <select value={lang} onChange={(e) => setLang(e.target.value as 'he' | 'en')}>
                  <option value="he">עברית</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
          </div>

          {clientId && (
            <div className="form-section">
              <div className="section-label">{t('create.select_entries')}</div>
              {entries.length === 0 ? (
                <div className="empty" style={{ padding: '12px 0' }}>
                  {t('create.no_unbilled')}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {entries.map((e) => {
                    const rate = e.hourlyRateIls ?? e.case?.hourlyRateIls ?? 0;
                    const lineShekels = (e.minutes / 60) * rate;
                    return (
                      <label
                        key={e.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '6px 8px',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 13,
                          background: selected.has(e.id) ? 'var(--accent-light)' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(e.id)}
                          onChange={() => toggle(e.id)}
                        />
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {e.case?.caseNumber ?? '—'}
                          {e.description && <span style={{ color: 'var(--text3)' }}> · {e.description}</span>}
                        </span>
                        <span style={{ color: 'var(--text2)', fontSize: 12 }}>
                          {(e.minutes / 60).toFixed(1)}h · ₪{lineShekels.toFixed(0)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {selected.size > 0 && (
            <div className="form-section">
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {t('create.summary', { total: totals.totalShekels.toFixed(0), hours: totals.hours })}
              </div>
            </div>
          )}

          {error && (
            <div className="form-section">
              <div className="signin-error">{error}</div>
            </div>
          )}

          <div className="form-footer">
            <button
              type="submit"
              className="btn-save"
              disabled={busy || selected.size === 0 || !clientId}
            >
              {busy ? t('create.submit_busy') : t('create.submit')}
            </button>
            <button type="button" className="btn-cancel" onClick={onClose} disabled={busy}>
              {t('common:actions.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
